import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { quoteApi, quoteSupplierItemApi } from "@/main/api";
import BoxCart from "@/main/components/shared/boxCart/boxCart";
import BoxCartNoSupplierAssigned from "@/main/components/shared/boxCart/boxCartNoSupplierAssigned";
import BoxCartUnansweredItems from "@/main/components/shared/boxCart/boxCartUnansweredItems";
import { AddCartType, RefreshQuoteByGetItemsType } from "@/main/components/shared/boxCart/components/types";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import { SpinnerLoading } from "@/main/components/shared/styles";
import { QuoteGetItemsFilterRequest, QuoteSupplierItemSaveCartRequest } from "@/main/models/request";
import { ProcurementStatusEnum } from "@/main/services/enum/StatusProcurement";
import { getGuidScreenByRoute, getTranslateByKey, isEmpty, isNullUndefined } from "@/main/services/utils";
import { formatCurrency } from "@/main/services/utilsNumber";
import { Cart, CartState } from "@/main/store/cart";
import {
	calculateTotalParts,
	calculateTotalPrice,
	calculateTotalShippingCost,
	destructGetItemsResponse,
	transformCartItemsToCart,
} from "@/main/store/cart/cartUtils";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { getRefreshForceUrl } from "@/main/services/utilsQueryParams";

import HeaderCollapseQuote from "./headerCollapseQuote/headerCollapseQuote";
import { DataQuoteStateType } from "./typesCollapseQuote";
import { INITIAL_CART_STATE } from "./utilsCollapseQuoteStep3";
import * as S from "./styles";
import BoxCartUnansweredCanceledItems from "@/main/components/shared/boxCart/boxCartUnansweredCanceledItems";
import BoxCartUnansweredRequotedItems from "@/main/components/shared/boxCart/boxCartUnansweredRequotedItems";
import InfoShippingCostAndTotal from "../infoShippingCostAndTotal/InfoShippingCostAndTotal";

interface ICollapseQuoteStep3Props {
	guidQuote: string;
	refreshClaimCounts: () => void;
	refreshClaim: () => void;
}
function CollapseQuoteStep3({ guidQuote, refreshClaimCounts, refreshClaim }: ICollapseQuoteStep3Props) {
	const navigate = useNavigate();

	const [dataQuote, setDataQuote] = useState<DataQuoteStateType>(null);
	const [dataCart, setDataCart] = useState<CartState>(INITIAL_CART_STATE);
	const [isLoadingQuote, setIsLoadingQuote] = useState<boolean>(false);
	const [isLoadingCart, setIsLoadingCart] = useState(false);

	const cartItems = dataCart.cartItems.data;
	const cartNoSupplierAssigned = dataCart.cartItems.dataNoSupplierAssigned;
	const cartDataUnansweredItems = dataCart.cartItems.dataUnansweredItems;
	const cartDataUnansweredCanceledItems = dataCart.cartItems.dataUnansweredCanceledItems;
	const cartDataUnansweredRequotedItems = dataCart.cartItems.dataUnansweredRequotedItems;

	async function getDetailsQuote(guidQuoteDetails: string, showLoading: boolean = false) {
		if (showLoading) setIsLoadingQuote(true);

		try {
			const response = await quoteApi.detailAwaiting(guidQuoteDetails);
			const {
				data: { result },
			} = response;

			const hasChangeStatus =
				!isNullUndefined(dataQuote) && dataQuote.idProcurementStatus !== result.idProcurementStatus;

			if (hasChangeStatus) {
				if (result.idProcurementStatus === ProcurementStatusEnum.Closed) {
					const urlRefresh = `${RoutesConstants.claimSearchClaims}${getRefreshForceUrl({ claimNumber: result.claimNumber })}`;
					navigate(urlRefresh);
					return;
				}

				refreshClaimCounts();
				refreshClaim();
			}
			setDataQuote(result);
		} catch {
		} finally {
			if (showLoading) setIsLoadingQuote(false);
		}
	}

	async function loadCart(showLoading: boolean = true) {
		if (showLoading) setIsLoadingCart(true);

		try {
			const request: QuoteGetItemsFilterRequest = {
				quoteGuid: guidQuote,
			};
			const response = await quoteApi.getItems(request);
			const {
				dataCart,
				dataCartNoSupplierAssigned,
				dataCartUnansweredItems,
				dataCartUnansweredCanceledItems,
				dataCartUnansweredRequotedItems,
			} = destructGetItemsResponse(response.data.result);

			const formattedDataCart = transformCartItemsToCart(dataCart, guidQuote);
			const updatedTotalSaved = calculateTotalPrice(formattedDataCart);
			const updatedShippingCostSaved = calculateTotalShippingCost(formattedDataCart);
			const updatedTotalPartsSaved = calculateTotalParts(formattedDataCart);
			const resultState: CartState = {
				errorMessagesGeneral: null,
				cartItems: {
					isLoading: false,
					isLoadingUpdate: false,
					idPartIsLoading: null,
					errorMessages: null,
					isError: false,
					data: formattedDataCart,
					dataNoSupplierAssigned: dataCartNoSupplierAssigned,
					dataUnansweredItems: dataCartUnansweredItems,
					dataUnansweredCanceledItems: dataCartUnansweredCanceledItems,
					dataUnansweredRequotedItems: dataCartUnansweredRequotedItems,
				},
				summary: {
					total: updatedTotalSaved,
					shippingCost: updatedShippingCostSaved,
					totalParts: updatedTotalPartsSaved,
				},
			};

			setDataCart(resultState);
		} finally {
			if (showLoading) setIsLoadingCart(false);
		}
	}

	async function loadInitialData() {
		if (isEmpty(guidQuote)) return;
		if (!isNullUndefined(dataQuote) && !isNullUndefined(dataCart)) return;
		setIsLoadingQuote(true);

		try {
			const response = await Promise.all([getDetailsQuote(guidQuote), loadCart()]);
			return response;
		} catch {
		} finally {
			setIsLoadingQuote(false);
		}
	}

	useEffect(() => {
		loadInitialData();
	}, [guidQuote]);

	const isQuoteBlocked = !isNullUndefined(dataQuote) && dataQuote.quoteBlocked;
	const isQuoteCanceled =
		!isNullUndefined(dataQuote) && dataQuote.idProcurementStatus === ProcurementStatusEnum.Cancelled;
	const integration = !isNullUndefined(dataQuote) && dataQuote.integration;

	const totalPriceCart = dataCart.summary.total;
	const totalCart = totalPriceCart;

	// Notation: the variable isPaymentPending is not used in this scope;
	const isPaymentPending = false;
	// Notation: this method is not used in this scope;
	function displayCompareSuppliers() {}

	const handleAddCart: AddCartType = async (items) => {
		try {
			const request: QuoteSupplierItemSaveCartRequest = {
				guid: items.parts[0].guid,
				externalPartId: items.parts[0].externalPartId,
				deliveryBySupplier: items.parts[0].deliveryBySupplier,
				guidPart: items.parts[0].guidPart,
				guidQuote: items.guidQuote,
				guidSupplier: items.guid,
				idPartType: items.parts[0].idPartType,
				guidSupplierInventoryWarehouse: items.parts[0].guidSupplierInventoryWarehouse,
				quantity: items.parts[0].quantity,
			};

			const response = await quoteSupplierItemApi.saveCart(request);

			if (response.data.result) {
				loadCart(true);
			}
		} catch {}
	};

	const refreshQuoteByGetItems: RefreshQuoteByGetItemsType = (result) => {
		const {
			dataCart,
			dataCartNoSupplierAssigned,
			dataCartUnansweredItems,
			dataCartUnansweredCanceledItems,
			dataCartUnansweredRequotedItems,
		} = destructGetItemsResponse(result);

		const formattedPart = transformCartItemsToCart(dataCart, String(guidQuote));
		const updatedTotalSaved = calculateTotalPrice(formattedPart);
		const updatedShippingCostSaved = calculateTotalShippingCost(formattedPart);
		const updatedTotalPartsSaved = calculateTotalParts(formattedPart);
		const resultState: CartState = {
			errorMessagesGeneral: null,
			cartItems: {
				isLoading: false,
				isLoadingUpdate: false,
				idPartIsLoading: null,
				errorMessages: null,
				isError: false,
				data: formattedPart,
				dataNoSupplierAssigned: dataCartNoSupplierAssigned,
				dataUnansweredItems: dataCartUnansweredItems,
				dataUnansweredCanceledItems: dataCartUnansweredCanceledItems,
				dataUnansweredRequotedItems: dataCartUnansweredRequotedItems,
			},
			summary: {
				total: updatedTotalSaved,
				shippingCost: updatedShippingCostSaved,
				totalParts: updatedTotalPartsSaved,
			},
		};

		setDataCart(resultState);

		const isCanceledQuote =
			isEmpty(dataCart) && isEmpty(dataCartNoSupplierAssigned) && isEmpty(dataCartUnansweredItems);

		if (isCanceledQuote) {
			getDetailsQuote(guidQuote);

			toast.success(getTranslateByKey("information.successfullyCanceledQuote"));
		}

		return {
			isCanceledQuote,
		};
	};

	if (isLoadingQuote) return <SpinnerLoading />;
	if (isNullUndefined(dataQuote)) return <ResultNotFoundComponent />;
	return (
		<S.Container>
			<HeaderCollapseQuote dataQuote={dataQuote} />
			{/* Items - No answered */}
			<BoxCartUnansweredItems
				dataCart={dataCart}
				integration={integration}
				idProcurementStatus={dataQuote.idProcurementStatus}
				guidQuote={String(guidQuote)}
				isPaymentPending={isPaymentPending}
				isQuoteBlocked={isQuoteBlocked}
				cart={cartDataUnansweredItems}
				loadCart={() => loadCart()}
				addCart={handleAddCart}
				refreshQuoteByGetItems={refreshQuoteByGetItems}
				showButtonRemoveBulkPart={false}
				showButtonRequoteBulkPart={false}
			/>

			{/* Items - NoSupplierAssigned */}
			<BoxCartNoSupplierAssigned
				dataCart={dataCart}
				idProcurementStatus={dataQuote.idProcurementStatus}
				integration={integration}
				guidQuote={String(guidQuote)}
				isPaymentPending={isPaymentPending}
				isQuoteBlocked={isQuoteBlocked}
				cart={cartNoSupplierAssigned}
				canShowButtonRemovePart={false}
				canShowButtonRequotePart={false}
				loadCart={() => loadCart()}
				addCart={handleAddCart}
				refreshQuoteByGetItems={refreshQuoteByGetItems}
			/>

			{/* Items - Cart */}
			{isLoadingCart ? (
				<SpinnerLoading />
			) : (
				cartItems.map((part: Cart, index: number) => (
					<BoxCart
						key={index}
						cart={part}
						guidScreen={getGuidScreenByRoute(RoutesConstants.claimSearchClaims)}
						dataCart={dataCart}
						showCompareSuppliers={false}
						canShowDeleteBoutton={false}
						guidQuote={guidQuote}
						isPaymentPending={isPaymentPending}
						isQuoteBlocked={isQuoteBlocked}
						displayCompareSuppliers={displayCompareSuppliers}
						isQuoteCanceled={isQuoteCanceled}
						refreshQuoteByGetItems={refreshQuoteByGetItems}
						idProcurementStatus={dataQuote.idProcurementStatus}
						integration={integration}
						addCart={handleAddCart}
					/>
				))
			)}

			<BoxCartUnansweredCanceledItems
				dataCart={dataCart}
				integration={integration}
				idProcurementStatus={dataQuote.idProcurementStatus}
				guidQuote={String(guidQuote)}
				isPaymentPending={isPaymentPending}
				isQuoteBlocked={isQuoteBlocked}
				cart={cartDataUnansweredCanceledItems}
				showButtonRequoteBulkPart={false}
				loadCart={() => loadCart()}
				addCart={handleAddCart}
				refreshQuoteByGetItems={refreshQuoteByGetItems}
			/>

			<BoxCartUnansweredRequotedItems cart={cartDataUnansweredRequotedItems} loadCart={() => loadCart()} />

			<InfoShippingCostAndTotal total={formatCurrency(totalCart)} />
		</S.Container>
	);
}

export default CollapseQuoteStep3;
